// src/pages/admin/Repairs.tsx
//
// Repairs desk — LIVE Shop backend (/api/repairs). No localStorage, no
// seed store: tickets are real Shop-owned rows audited by kc sub. Staff
// can read the queue; advancing and creating require the admin write
// role (the API fails closed with 403 otherwise and the error surfaces
// in the form).

import { useCallback, useEffect, useState } from "react";
import { Wrench, ChevronRight, Plus, RefreshCcw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge, type BadgeVariant } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardContent } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/EmptyState";
import { Input } from "@/components/ui/Input";
import { CustomerPicker, type CustomerSelection } from "@/components/opsdesk/CustomerPicker";
import { useAuth } from "@/components/auth/AuthContext";
import { cn } from "@/lib/cn";
import {
  advanceRepair,
  createRepair,
  fetchRepairs,
  REPAIR_NEXT_STATUS,
  type RepairStatus,
  type RepairTicketRow,
} from "@/lib/opsdesk/api";

const STATUS_LABEL_KEY: Record<RepairStatus, string> = {
  triage: "admin.repairs.statusTriage",
  "in-repair": "admin.repairs.statusInRepair",
  "vendor-routing": "admin.repairs.statusVendorRouting",
  closed: "admin.repairs.statusClosed",
};

const STATUS_VARIANT: Record<RepairStatus, BadgeVariant> = {
  triage: "warning",
  "in-repair": "teal",
  "vendor-routing": "info",
  closed: "default",
};

const STATUS_ORDER: RepairStatus[] = [
  "triage",
  "in-repair",
  "vendor-routing",
  "closed",
];

export function AdminRepairsQueue() {
  const auth = useAuth();
  const { t } = useTranslation();
  const [tickets, setTickets] = useState<RepairTicketRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState<RepairStatus | "all">("all");
  const [showCreate, setShowCreate] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const body = await fetchRepairs(statusFilter);
      setTickets(body.rows);
    } catch (e) {
      setError(String(e instanceof Error ? e.message : e));
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    if (auth.isReady && auth.isAuthenticated) void refresh();
  }, [auth.isReady, auth.isAuthenticated, refresh]);

  async function doAdvance(ticket: RepairTicketRow) {
    setBusyId(ticket.ticket_id);
    setError("");
    try {
      await advanceRepair(ticket.ticket_id);
      await refresh();
    } catch (e) {
      setError(String(e instanceof Error ? e.message : e));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Wrench
              className="h-5 w-5 text-benchmark-teal-bright"
              strokeWidth={1.5}
            />
            <h1 className="text-2xl font-510 tracking-tight text-fg-primary">
              {t("admin.repairs.title")}
            </h1>
          </div>
          <p className="mt-1 text-sm text-fg-tertiary">
            {tickets.length} {tickets.length === 1 ? t("admin.repairs.ticketOne") : t("admin.repairs.ticketOther")}
            {statusFilter !== "all"
              ? ` ${t("admin.repairs.withStatus")} ${t(STATUS_LABEL_KEY[statusFilter])}`
              : ""}
            {t("admin.repairs.statusNote")}
          </p>
        </div>
        <Button size="sm" onClick={() => setShowCreate((v) => !v)}>
          <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
          {t("admin.repairs.newTicket")}
        </Button>
      </header>

      {showCreate && (
        <CreateRepairForm
          onCreated={() => {
            setShowCreate(false);
            void refresh();
          }}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {error && (
        <div className="rounded-md border border-accent-rose/30 bg-accent-rose/10 p-3 text-sm text-accent-rose">
          {error}
        </div>
      )}

      {/* Status filter */}
      <div className="flex flex-wrap items-center gap-1.5 text-xs">
        <span className="text-fg-quaternary">{t("admin.repairs.statusLabel")}</span>
        {(["all", ...STATUS_ORDER] as Array<RepairStatus | "all">).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatusFilter(s)}
            aria-pressed={statusFilter === s}
            className={cn(
              "rounded px-2 py-1 font-510 uppercase tracking-wider transition-colors",
              statusFilter === s
                ? "bg-benchmark-teal/20 text-benchmark-teal-bright"
                : "bg-bg-elevated text-fg-secondary hover:bg-bg-hover hover:text-fg-primary",
            )}
          >
            {s === "all" ? t("admin.repairs.all") : t(STATUS_LABEL_KEY[s])}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="rounded-md border border-border bg-bg-canvas/30 p-3 text-sm text-fg-tertiary">
          <RefreshCcw className="mr-2 inline h-3.5 w-3.5 animate-spin" />
          {t("admin.repairs.loadingTickets")}
        </div>
      ) : tickets.length === 0 ? (
        <EmptyState
          icon={
            <Wrench className="h-5 w-5 text-accent-indigo" strokeWidth={1.5} />
          }
          title={t("admin.repairs.emptyTitle")}
          description={t("admin.repairs.emptyDescription")}
        />
      ) : (
        <div className="space-y-2.5">
          {tickets.map((ticket) => (
            <Card key={ticket.ticket_id}>
              <CardContent className="grid gap-3 p-4 sm:grid-cols-[1.2fr_2fr_auto] sm:items-center">
                <div className="min-w-0">
                  <div className="font-mono text-[10px] text-fg-quaternary">
                    {ticket.ticket_id}
                  </div>
                  <div className="mt-0.5 truncate text-sm font-510 text-fg-primary">
                    {ticket.customer_name}
                  </div>
                  {ticket.product_id !== null && (
                    <div className="font-mono text-[11px] text-fg-tertiary">
                      {t("admin.repairs.productRef")}{ticket.product_id}
                    </div>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Badge variant={STATUS_VARIANT[ticket.status]} size="sm">
                    {t(STATUS_LABEL_KEY[ticket.status])}
                  </Badge>
                  <p className="line-clamp-2 text-[11px] text-fg-tertiary">
                    {ticket.subject}
                  </p>
                  {ticket.note && (
                    <p className="line-clamp-1 text-[10px] text-fg-quaternary">
                      {ticket.note}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {REPAIR_NEXT_STATUS[ticket.status] ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={busyId === ticket.ticket_id}
                      onClick={() => void doAdvance(ticket)}
                    >
                      {busyId === ticket.ticket_id ? (
                        <RefreshCcw className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
                      ) : (
                        <ChevronRight className="h-3.5 w-3.5" strokeWidth={1.5} />
                      )}
                      {t("admin.repairs.moveTo")} {t(STATUS_LABEL_KEY[REPAIR_NEXT_STATUS[ticket.status]!])}
                    </Button>
                  ) : (
                    <span className="text-[10px] uppercase tracking-wider text-fg-quaternary">
                      {t("admin.repairs.statusClosed")}
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function CreateRepairForm({
  onCreated,
  onCancel,
}: {
  onCreated: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [customer, setCustomer] = useState<CustomerSelection | null>(null);
  const [productId, setProductId] = useState("");
  const [subject, setSubject] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const productIdTrimmed = productId.trim();
  const productIdValid =
    productIdTrimmed === "" || /^\d+$/.test(productIdTrimmed);
  const canSubmit =
    customer !== null && subject.trim().length > 0 && productIdValid && !busy;

  async function submit() {
    if (!customer) return;
    setBusy(true);
    setError("");
    try {
      await createRepair({
        customer_id: customer.customer_id,
        product_id: productIdTrimmed === "" ? null : Number(productIdTrimmed),
        subject: subject.trim(),
        note: note.trim() || null,
      });
      onCreated();
    } catch (e) {
      setError(String(e instanceof Error ? e.message : e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <h3 className="text-xs font-510 uppercase tracking-wider text-fg-tertiary">
          {t("admin.repairs.newRepairTicket")}
        </h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <label className="block text-[10px] font-510 uppercase tracking-wider text-fg-tertiary">
              {t("admin.repairs.customer")}
            </label>
            <CustomerPicker value={customer} onChange={setCustomer} />
          </div>
          <div className="space-y-1">
            <label className="block text-[10px] font-510 uppercase tracking-wider text-fg-tertiary">
              {t("admin.repairs.productIdOptional")}
            </label>
            <Input
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
              placeholder={t("admin.repairs.productIdPlaceholder")}
              inputMode="numeric"
            />
            {!productIdValid && (
              <p className="text-[10px] text-accent-rose">
                {t("admin.repairs.productIdNumber")}
              </p>
            )}
          </div>
        </div>
        <div className="space-y-1">
          <label className="block text-[10px] font-510 uppercase tracking-wider text-fg-tertiary">
            {t("admin.repairs.subject")}
          </label>
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder={t("admin.repairs.subjectPlaceholder")}
          />
        </div>
        <div className="space-y-1">
          <label className="block text-[10px] font-510 uppercase tracking-wider text-fg-tertiary">
            {t("admin.repairs.noteOptional")}
          </label>
          <textarea
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="w-full rounded border border-border bg-bg-surface px-3 py-2 text-xs text-fg-primary placeholder:text-fg-quaternary focus:border-benchmark-teal/60 focus:outline-none"
          />
        </div>
        {error && (
          <div className="rounded-md border border-accent-rose/30 bg-accent-rose/10 p-2 text-xs text-accent-rose">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel}>
            {t("admin.repairs.cancel")}
          </Button>
          <Button size="sm" disabled={!canSubmit} onClick={() => void submit()}>
            {t("admin.repairs.createTicket")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
